/**
 * /pay/cancelled — user cancelled out of Stripe Checkout
 *
 * Stripe redirects here when the user hits "Back" on the Stripe Checkout page.
 * We pass ?token=<token> as the cancel_url so we can offer a "Return to invoice" link.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { BrandHeader } from '@/components/payment/BrandHeader';
import { Button } from '@/components/ui/Button';

interface CancelledSearch {
  token?: string;
}

export const Route = createFileRoute('/pay/cancelled')({
  validateSearch: (search: Record<string, unknown>): CancelledSearch => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
  component: PayCancelledPage,
});

function PayCancelledPage() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col items-center justify-start px-4 py-10">
      <div className="w-full max-w-lg">
        <BrandHeader />
        <main>
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-6 py-8 text-center">
            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Payment cancelled.</p>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Your invoice is still available.</p>
            {token && (
              <Button
                variant="primary"
                className="mt-6"
                onClick={() => void navigate({ to: '/pay/$token', params: { token } })}
              >
                Return to invoice
              </Button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
